import { Editor } from "@tinymce/tinymce-react";
import { debounce } from "lodash";
import { Card } from "primereact/card";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { generateUnderwritingPdf } from "../../../api/routes";
import { useToast } from "../../../context-providers/Toast";
import { ThemeContext } from "../../../themes/Theme";
import { downloadBlob } from "../../../util";
import { SafeReportResponse } from "../Conversion";
import { useCalculatedPremium, useUpdateReport } from "../data";
import { buildSnapshotReferralHtml } from "./snapshotTemplate";

type ReferralCardProps = {
  specialtyPropertyInfo: SafeReportResponse;
};

export const ReferralCard: React.FC<ReferralCardProps> = ({
  specialtyPropertyInfo,
}) => {
  // The pricing rows depend on the calculated premium, which loads async.
  const premiumQuery = useCalculatedPremium(
    specialtyPropertyInfo.report_id,
    true
  );
  const premiumDataRef = useRef(premiumQuery.data);
  premiumDataRef.current = premiumQuery.data;

  const [text, setText] = useState<string | null>(
    () => specialtyPropertyInfo.report_json.referral_text?.text ?? null
  );

  // When there's no saved referral yet, seed the snapshot once the premium
  // query settles so the pricing row is populated (or cleanly omitted on error).
  // Once `text` is set it is never auto-regenerated, preserving user edits.
  useEffect(() => {
    if (text === null && !premiumQuery.isLoading) {
      setText(
        buildSnapshotReferralHtml(specialtyPropertyInfo, premiumQuery.data)
      );
    }
  }, [text, premiumQuery.isLoading, premiumQuery.data, specialtyPropertyInfo]);

  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";

  const toast = useToast();
  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);

  const [saveState, setSaveState] = useState<{
    dirty: boolean;
    lastSaveTime: number | undefined;
  }>({ dirty: false, lastSaveTime: undefined });

  const saveIcon = (
    <span
      className={
        updateReport.isPending
          ? "pi pi-spin pi-spinner"
          : saveState.dirty
            ? "pi pi-file-edit"
            : "pi pi-check"
      }
      style={{ fontSize: "0.75rem" }}
    />
  );
  const saveMessage = updateReport.isPending
    ? "Saving..."
    : saveState.dirty
      ? "Unsaved changes"
      : saveState.lastSaveTime
        ? "Saved at " + new Date(saveState.lastSaveTime).toLocaleTimeString()
        : "Saved (No changes)";

  const saveStatus = (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "12px",
      }}
    >
      {saveIcon} {saveMessage}
    </span>
  );

  const debouncedUpdate = useCallback(
    debounce((content) => {
      updateReport.mutateAsync(
        { referralText: { text: content } },
        {
          onSuccess: () =>
            setSaveState({ dirty: false, lastSaveTime: Date.now() }),
        }
      );
    }, 1000),
    []
  );

  return (
    <Card title="Referral" subTitle={saveStatus}>
      {text === null ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "16px",
          }}
        >
          <span className="pi pi-spin pi-spinner" /> Preparing referral…
        </div>
      ) : (
        <Editor
          value={text}
          init={{
            ...{
              height: 500,
              menubar: false,
              plugins: [
                "advlist",
                "autolink",
                "lists",
                "link",
                "charmap",
                "table",
                "help",
                "wordcount",
              ],
              toolbar:
                "bold italic underline | table | numlist bullist | customcopy customdownload clearedits",
              setup: (editor) => {
                editor.ui.registry.addButton("clearedits", {
                  icon: "reload",
                  tooltip: "Clear all Edits",
                  onAction: async () => {
                    updateReport.mutateAsync(
                      {
                        referralText: { text: null },
                      },
                      {
                        onSuccess: () =>
                          setSaveState({
                            dirty: false,
                            lastSaveTime: Date.now(),
                          }),
                      }
                    );

                    setText(
                      buildSnapshotReferralHtml(
                        specialtyPropertyInfo,
                        premiumDataRef.current
                      )
                    );
                  },
                });
                editor.ui.registry.addButton("customdownload", {
                  icon: "export-pdf",
                  tooltip: "Download as PDF",
                  onAction: async () => {
                    const html = editor.getContent({ format: "html" });
                    toast.show({
                      severity: "info",
                      summary: "Generating report",
                      detail: "Adding screenshots...",
                      icon: "pi pi-spin pi-spinner",
                    });
                    try {
                      const blob = await generateUnderwritingPdf(
                        specialtyPropertyInfo.report_id,
                        html
                      );
                      downloadBlob(
                        specialtyPropertyInfo.report_json.company_info
                          .company_name,
                        blob
                      );
                      toast.replace({
                        severity: "success",
                        summary: "Report downloaded",
                        detail: "You can find the report in your downloads.",
                        life: 5000,
                      });
                    } catch {
                      toast.replace({
                        severity: "error",
                        summary: "Something went wrong",
                        life: 5000,
                      });
                    }
                  },
                });
                editor.ui.registry.addButton("customcopy", {
                  icon: "copy",
                  tooltip: "Copy content",
                  onAction: async () => {
                    const content = editor.getContent({ format: "html" });
                    const textContent = editor.getContent({ format: "text" });
                    const htmlBlob = new Blob([content], { type: "text/html" });
                    const plainBlob = new Blob([textContent], {
                      type: "text/plain",
                    });

                    try {
                      await navigator.clipboard.write([
                        new ClipboardItem({
                          "text/html": htmlBlob,
                          "text/plain": plainBlob,
                        }),
                      ]);
                    } catch (err) {
                      // Fallback to text-only if clipboard.write not supported
                      navigator.clipboard.writeText(content);
                    }

                    toast.show({
                      severity: "success",
                      summary: "Copied",
                      detail: "Message copied to clipboard",
                      life: 5000,
                    });
                  },
                });
              },
              content_style:
                'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px }',
            },
            ...(isLightMode ? {} : { skin: "oxide-dark", content_css: "dark" }),
          }}
          onInit={(_, editor) => {
            editor.on("input", () => {
              // Only fires on user input
              const content = editor.getContent();
              setSaveState((x) => ({ ...x, dirty: true }));
              debouncedUpdate(content);
            });
          }}
          onEditorChange={(content) => {
            setText(content);
          }}
        />
      )}
    </Card>
  );
};
